import { useEffect, useRef, useState, ReactNode } from 'react';

interface RevealProps {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: keyof JSX.IntrinsicElements;
}

export const Reveal = ({ children, delay = 0, className = '', as: Tag = 'div' }: RevealProps) => {
  const ref = useRef<HTMLElement | null>(null);
  // Start as "ready/visible" so SSR/no-JS/crawler renders show content.
  // Only enter the hidden→reveal animation if JS runs and motion is allowed.
  const [enabled, setEnabled] = useState(false);
  const [shown, setShown] = useState(true);

  useEffect(() => {
    // Respect reduced motion: skip the animation entirely.
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      return;
    }

    // Enable the animation: hide first, then reveal on intersect.
    setEnabled(true);
    setShown(false);

    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShown(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const Component = Tag as any;
  const style = enabled
    ? {
        opacity: shown ? 1 : 0,
        transform: shown ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 400ms ease-out ${delay}ms, transform 400ms ease-out ${delay}ms`,
      }
    : undefined;

  return (
    <Component ref={ref} className={className} style={style}>
      {children}
    </Component>
  );
};
